// reportPackage.js — builds ONE official package PDF at PDF level (pdf-lib) and stores it in the private
// report-packages bucket, versioned by the report snapshot. Appendix PDFs (Section 4/5 + Supporting Evidence
// Pack) are COPIED as native pages — NEVER rasterised. The report body is a small raster (MVP).
//
// CHECKPOINT 3: library only. Nothing wires this into approval / View Report / client acknowledgement / WSHC
// yet, and the old raster flow (reportAppendix.js) is left completely intact as the fallback.
//
// pdf-lib is loaded LAZILY (dynamic import) inside generateOfficialPackage — so importing this module (which
// PackageViewer does statically) does NOT pull pdf-lib into the main bundle; it loads only on generate.
import { supabase } from './supabase'
import { buildAppendixList } from './reportAppendix'
import { bodyToPageImages, A4 } from './reportBodyPdf'

export const PACKAGES_BUCKET = 'report-packages'
export const STALE_MS = 10 * 60 * 1000   // a 'preparing' older than this is treated as re-triggerable

// Storage path for a job's package at a given snapshot version. The file carries the report's download name
// (e.g. 1526-Audit-Report-v1.pdf); without a name it falls back to the old "official-package.pdf".
export function packagePath(jobId, version, fileName) {
  return `${jobId}/v${version}/${fileName || 'official-package.pdf'}`
}

// True when a 'preparing' row is stale (started too long ago, or no start stamp) → safe to restart.
export function isPreparingStale(snap) {
  if (!snap || snap.package_status !== 'preparing') return false
  const started = snap.package_started_at ? new Date(snap.package_started_at).getTime() : 0
  return !started || (Date.now() - started) > STALE_MS
}

// Patch the package_* fields on the specific (job_id, version) snapshot row.
async function setStatus(jobId, version, patch) {
  await supabase.from('audit_report_snapshots').update(patch).eq('job_id', jobId).eq('version', version)
}

// Read the raw bytes of a stored appendix PDF. audit-evidence ORIGINALS are only READ here, never modified.
async function fetchPdfBytes(storagePath) {
  const { data } = await supabase.storage.from('audit-evidence').createSignedUrl(storagePath, 300)
  if (!data?.signedUrl) throw new Error(`Could not sign appendix: ${storagePath}`)
  const res = await fetch(data.signedUrl)
  if (!res.ok) throw new Error(`Fetch failed (${res.status}) for ${storagePath}`)
  return await res.arrayBuffer()
}

// Generate + store the official package for (jobId, snapshot version).
//   bodyElement      = the rendered ReportDocument DOM node (for the MVP raster body)
//   snapshotEvidence = the snapshot's frozen evidence list (drives buildAppendixList order)
// Idempotent: overwrites the same version path and re-sets status. NEVER throws to the caller — failures are
// captured into package_status='error' and returned as { ok:false, error }.
export async function generateOfficialPackage({ jobId, version, bodyElement, snapshotEvidence, fileName }) {
  try {
    // Kill switch — never generate when the feature is disabled firm-wide.
    const { data: fs } = await supabase.from('firm_settings').select('official_package_enabled').eq('id', 1).maybeSingle()
    if (fs && fs.official_package_enabled === false) return { ok: false, error: 'disabled' }

    await setStatus(jobId, version, {
      package_status: 'preparing',
      package_started_at: new Date().toISOString(),
      package_error: null,
    })

    const { PDFDocument } = await import('pdf-lib')   // lazy: loaded only on generate

    // 1) Report body → A4 page images (MVP raster; small, drift-free).
    const bodyPages = await bodyToPageImages(bodyElement)

    // 2) Assemble at PDF level. Body pages first, then each appendix PDF copied AS-IS (no rasterisation),
    //    in the report's section order (Section 4 → Section 5 → Supporting Evidence Pack).
    const out = await PDFDocument.create()
    for (const p of bodyPages) {
      const jpg = await out.embedJpg(p.dataUrl)
      const page = out.addPage([A4.w, A4.h])
      const drawW = A4.w
      const drawH = drawW * (p.hPx / p.wPx)
      page.drawImage(jpg, { x: 0, y: A4.h - drawH, width: drawW, height: drawH })
    }
    for (const a of buildAppendixList(snapshotEvidence || [])) {
      const path = a?.ev?.storage_path
      if (!path) continue
      const isPdf = /\.pdf($|\?)/i.test(a.ev.file_name || '') || /\.pdf($|\?)/i.test(path)
      if (!isPdf) continue
      const src = await PDFDocument.load(await fetchPdfBytes(path), { ignoreEncryption: true })
      const copied = await out.copyPages(src, src.getPageIndices())
      copied.forEach(pg => out.addPage(pg))
    }

    // 3) Upload to the PRIVATE report-packages bucket (upsert = overwrite the same version).
    const path = packagePath(jobId, version, fileName)
    const outBytes = await out.save()
    const { error: upErr } = await supabase.storage.from(PACKAGES_BUCKET)
      .upload(path, new Blob([outBytes], { type: 'application/pdf' }), { upsert: true, contentType: 'application/pdf' })
    if (upErr) throw new Error(`Upload failed: ${upErr.message}`)

    await setStatus(jobId, version, {
      package_status: 'ready',
      package_path: path,
      package_generated_at: new Date().toISOString(),
      package_error: null,
    })
    return { ok: true, path }
  } catch (e) {
    const msg = (e && e.message) ? e.message : String(e)
    try { await setStatus(jobId, version, { package_status: 'error', package_error: msg }) } catch (_) {}
    return { ok: false, error: msg }
  }
}
